import { SmartFormLogic } from "../../../../swaggerServices";

export interface SmartFormLogicExtended extends SmartFormLogic {
  currentlyEvaluating?: boolean;
  computedResult?: boolean;
}


const evaluateOperator: { [key in SmartFormLogic.OperatorEnum]: Function } = {
  equals: (answer, comparison) => {
    return answer.toString() === comparison.toString();
  },
  notEquals: (answer, comparison) => {
    return answer.toString() !== comparison.toString();
  },
  contains: (answer, comparison) => {
    const text = answer.toLowerCase() as string;
    const subText = comparison && comparison.toLowerCase() as string;
    return text.includes(subText);
  },
  greaterThan: (answer, comparison) => {
    return +answer > +comparison;
  },
  lessThan: (answer, comparison) => {
    return +answer < +comparison;
  },
  answered: (answer) => {
    return answer?.toString()?.length > 0;
  },
}

function evaluateAndList(nodes: Array<SmartFormLogicExtended>, questionAnswers): boolean {
  if (!nodes || nodes.length === 0) {
    return true; // Empty and list evaluates to true
  }
  return nodes.every(node => evaluateNode(node, questionAnswers, true));
}

function evaluateOrList(nodes: Array<SmartFormLogicExtended>, questionAnswers): boolean {
  if (!nodes || nodes.length === 0) {
    return false; // Empty or list evaluates to false
  }
  return nodes.some(node => evaluateNode(node, questionAnswers));
}

export function evaluateNode(node: SmartFormLogicExtended, questionAnswers: any, andSearch = false): boolean {
  if (node.computedResult != undefined) {
    if (andSearch && node.currentlyEvaluating) {
      throw Error(`Logic Node with id "${node.id}" has been found in a deep recursive loop! Check the configuration of all its "orNodes"`)
    }
    else {
      return node.computedResult;
    }
  }
  else if (node.currentlyEvaluating) {
    throw Error(`Logic Node with id "${node.id}" has been found in a recursive loop! Check the configuration of its "andNodes"`)
  }


  node.currentlyEvaluating = true;

  const { questionId, operator, value, andChildren, orChildren } = node;
  let answer = questionAnswers[`${questionId}`];

  let result: boolean
  if (answer) {
    result = evaluateOperator[operator](answer, value);
  }


  // if true, also check and clauses
  if (result) {
    result = evaluateAndList(andChildren, questionAnswers);
  }

  node.computedResult = result;

  // if not already true, check or clauses
  if (!node.computedResult) {
    node.computedResult = evaluateOrList(orChildren, questionAnswers);
  }

  node.currentlyEvaluating = false;
  return node.computedResult;
}

export function resetNodeResultCache(node: SmartFormLogicExtended) {
  node.currentlyEvaluating = false;
  node.computedResult = undefined;
  node.andChildren && node.andChildren.forEach(n => resetNodeResultCache(n));
  node.orChildren && node.orChildren.forEach(n => resetNodeResultCache(n));
}

export const getTriggeringQuestionsForNode = (node: SmartFormLogicExtended): number[] => {
  const childQuestionIds = [];
  if (node.andChildren) {
    node.andChildren.forEach(child => {
      childQuestionIds.push(...getTriggeringQuestionsForNode(child));
    });
  }
  if (node.orChildren) {
    node.orChildren.forEach(child => {
      childQuestionIds.push(...getTriggeringQuestionsForNode(child));
    });
  }
  return [...new Set([node.questionId, ...childQuestionIds])];
}
/**
 * Every time a question is answered, we need to get the related logic block to re-evaluate.
 * This function create a questionId and rootIds map so that we can get the related blocks quickly from the questionId.
 * @param nodes
 * @returns format:
 * {
 *   [questionId1]: [rootNodeId1, rootNodeId2],
 *   [questionId2]: [rootNodeId3, rootNodeId4],
 * }
 */
export const getTriggeringQuestionIdAndRootNodeIdMap = (nodes: SmartFormLogicExtended[]) => {
  const triggerQuestionIds = {};
  nodes.forEach(node => {
    resetNodeResultCache(node)
    const nodeTriggerQuestionIds = getTriggeringQuestionsForNode(node);
    nodeTriggerQuestionIds.forEach(questionId => {
      const triggeredBlocks = triggerQuestionIds[`${questionId}`] || [];
      triggeredBlocks.push(node.id);
      triggerQuestionIds[`${questionId}`] = triggeredBlocks;
    });
  });

  return triggerQuestionIds;
}

export const getVisibleQuestionsInSmartForms = (smartForms) => {
  const visibleQuestions = {}
  smartForms.forEach(sf => {
    const questionAnswerMap = getQuestionAnswersFromSmartForm(sf);
    const computedResultMap = evaluateConditionalLogics(sf, questionAnswerMap);
    sf.smartFormCategories.forEach(sfCategory => {
      sfCategory.smartFormSubCategories.forEach(sfSubcategory => {
        sfSubcategory.smartFormQuestions.forEach(question => {
          if(question.showByDefault || computedResultMap[`question-${question.questionId}`]) {
            visibleQuestions[question.questionId] = question
          }
        })
      })
    })
  })
  return visibleQuestions
}

export const evaluateConditionalLogics = (smartForm, questionAnswers) => {
  const computedResultMap = {};
  const smartFormLogicNodes: SmartFormLogicExtended[] = smartForm.SmartFormLogic;

  const buildKey = (entity, entityId)=> {
    return `${entity}-${entityId}`;
  }

  for (const node of smartFormLogicNodes) {
    evaluateNode(node, questionAnswers);
    if (!node.computedResult) {
      continue;
    }
    for (const action of node.actions) {
      const computedResultKey = buildKey(action.entity, action.entityId);
      const computedResult = computedResultMap[computedResultKey] || {};
      computedResult[`${node.id}`] = {
        show: action.show,
        mandatory: action.mandatory,
        triggeringQuestionIds: getTriggeringQuestionsForNode(node),
      }
      computedResultMap[computedResultKey] = computedResult;
    }
  }
  return computedResultMap;
}

export const getQuestionAnswersFromSmartForm = (smartForm) => {
  const questionAnswers = {};
  smartForm.smartFormCategories.forEach(smartFormCategory => {
    smartFormCategory.smartFormSubCategories.forEach(smartFormSubCategory => {
      smartFormSubCategory.smartFormQuestions.forEach(smartFormQuestion => {
        questionAnswers[`${smartFormQuestion.questionId}`] = smartFormQuestion.answers ? smartFormQuestion.answers[0]?.answer: null;
      });
    });
  });
  return questionAnswers;
}